import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from './Button.jsx';
import { useCreateMovement, useUpdateMovement } from '../hooks/useAssociateAccounts.js';

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

const KIND_OPTIONS = [
  { value: 'contribution',    label: 'Apport' },
  { value: 'withdrawal',      label: 'Retrait' },
  { value: 'adjustment',      label: 'Régularisation' },
  { value: 'opening_balance', label: 'Reprise d\'antériorité' },
];

function Field({ label, children, error, hint, required }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    kind:          'contribution',
    amount:        '',
    movement_date: todayIso(),
    label:         '',
    is_historical: false,
  };
}

function formFromMovement(movement) {
  if (!movement) return emptyForm();
  return {
    kind:          movement.kind ?? 'contribution',
    amount:        movement.amount_cents != null ? String(movement.amount_cents / 100) : '',
    movement_date: movement.movement_date ?? todayIso(),
    label:         movement.label ?? '',
    is_historical: movement.is_historical ?? false,
  };
}

function validate(form) {
  const errors = {};
  if (!form.kind) errors.kind = 'Type requis';
  const amount = parseFloat(form.amount);
  if (!form.amount || isNaN(amount) || amount <= 0) {
    errors.amount = 'Montant requis (> 0)';
  }
  if (!form.movement_date) errors.movement_date = 'Date requise';
  return errors;
}

/**
 * AssociateMovementForm — slide-over modal pour saisir ou éditer un mouvement
 * sur une tranche de compte courant d'associé.
 *
 * Props :
 *   open      : boolean
 *   onClose   : () => void
 *   accountId : string — UUID du compte
 *   trancheId : string — UUID de la tranche (contexte de la modale)
 *   movement  : object | undefined — si fourni, mode édition
 */
export default function AssociateMovementForm({ open, onClose, accountId, trancheId, movement }) {
  const isEdit = Boolean(movement);
  const createMovement = useCreateMovement();
  const updateMovement = useUpdateMovement();
  const mutation = isEdit ? updateMovement : createMovement;

  const [form, setForm]     = useState(emptyForm);
  const [errors, setErrors] = useState({});

  // opening_balance force is_historical = true
  const isOpeningBalance = form.kind === 'opening_balance';

  useEffect(() => {
    if (open) {
      setForm(formFromMovement(movement));
      setErrors({});
      mutation.reset?.();
    }
  }, [open, movement?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Quand kind devient opening_balance, forcer is_historical
      if (field === 'kind' && value === 'opening_balance') {
        next.is_historical = true;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const amount_cents = Math.round(parseFloat(form.amount) * 100);
    const is_historical = isOpeningBalance ? true : form.is_historical;

    if (isEdit) {
      await mutation.mutateAsync({
        accountId,
        trancheId,
        movementId:    movement.id,
        kind:          form.kind,
        amount_cents,
        movement_date: form.movement_date,
        label:         form.label.trim() || null,
        is_historical,
      });
    } else {
      await mutation.mutateAsync({
        accountId,
        trancheId,
        kind:          form.kind,
        amount_cents,
        movement_date: form.movement_date,
        label:         form.label.trim() || null,
        is_historical,
      });
    }

    onClose?.();
  }

  const isDisabled = mutation.isPending;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 bg-black/30
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Modifier le mouvement' : 'Nouveau mouvement'}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[95dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[480px] lg:rounded-none lg:max-h-none
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Modifier le mouvement' : 'Nouveau mouvement'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-5 space-y-4">
          {/* Type de mouvement */}
          <Field label="Type" error={errors.kind} required>
            <div className="grid grid-cols-2 gap-2">
              {KIND_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`
                    flex items-center gap-2 rounded-lg border px-3 py-2.5 cursor-pointer text-sm transition-colors
                    ${form.kind === opt.value
                      ? 'border-slate-800 bg-slate-800 text-white'
                      : 'border-slate-200 text-slate-700 hover:border-slate-400'}
                  `}
                >
                  <input
                    type="radio"
                    name="kind"
                    value={opt.value}
                    checked={form.kind === opt.value}
                    onChange={() => set('kind', opt.value)}
                    disabled={isDisabled}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </Field>

          {/* Note sur le sens de adjustment */}
          {form.kind === 'adjustment' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
              La régularisation diminue le solde de la tranche (sens négatif). Utilisez
              un Apport pour une correction positive.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Montant (€)" error={errors.amount} required>
              <input
                type="number"
                className={inputClass}
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="1000.00"
                disabled={isDisabled}
              />
            </Field>

            <Field label="Date" error={errors.movement_date} required>
              <input
                type="date"
                className={inputClass}
                value={form.movement_date}
                onChange={(e) => set('movement_date', e.target.value)}
                disabled={isDisabled}
              />
            </Field>
          </div>

          <Field label="Libellé (optionnel)">
            <input
              type="text"
              className={inputClass}
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder="Ex : Virement du 15 janvier"
              disabled={isDisabled}
            />
          </Field>

          {/* Mouvement historique */}
          <label
            className={`flex items-start gap-3 cursor-pointer rounded-lg border px-3 py-2.5 text-sm transition-colors
              ${isOpeningBalance ? 'bg-slate-50 border-slate-200 opacity-75' : 'border-slate-200 hover:border-slate-400'}`}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-800 focus:ring-slate-500 shrink-0"
              checked={isOpeningBalance ? true : form.is_historical}
              onChange={(e) => !isOpeningBalance && set('is_historical', e.target.checked)}
              disabled={isDisabled || isOpeningBalance}
            />
            <div>
              <p className="font-medium text-slate-700">Mouvement historique (hors Qonto)</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Ce mouvement n'a pas de contrepartie dans Qonto. Il ne pourra pas être lié à une
                transaction.
                {isOpeningBalance && ' Automatiquement activé pour les reprises d\'antériorité.'}
              </p>
            </div>
          </label>

          {mutation.error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {mutation.error.message}
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-safe">
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={isDisabled}
              className="flex-1"
            >
              {isEdit ? 'Enregistrer' : 'Enregistrer le mouvement'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isDisabled}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
